import { useState } from "react";
import type { NumberLineBlock } from "@shared/lessonBlocks";
import { RichText } from "../RichText";
import { NumberLineSvg } from "../Visuals";
import { useCheck, CheckFooter, type CheckResult } from "../CheckShell";

interface Props {
  block: NumberLineBlock;
  onResult: (r: CheckResult) => void;
  alreadyDone?: CheckResult | null;
}

export function NumberLineBlockView({ block, onResult, alreadyDone }: Props) {
  const check = useCheck(onResult, { alreadyDone });
  const [value, setValue] = useState<number | null>(null);
  const done = check.locked;
  const tol = block.tolerance ?? Math.min(block.step / 2, 1e-6) + 1e-9;
  const submit = () => check.submit(value !== null && Math.abs(value - block.target) <= tol);
  const shown = done && check.status === "revealed" ? block.target : value;
  const fmt = (v: number) => Number.isInteger(v) ? v.toLocaleString("en-JM").replace(/,/g, " ") : String(v);

  return (
    <div className="space-y-4">
      <RichText text={block.prompt} className="text-lg md:text-xl font-semibold" />
      <p className="text-sm text-muted-foreground">Tap or drag along the line to place the marker.</p>
      <div className={`rounded-2xl border-2 p-3 ${done ? (check.status === "correct" ? "border-emerald-400" : "border-sky-400") : "border-border bg-card"}`}>
        <NumberLineSvg
          min={block.min}
          max={block.max}
          step={block.step}
          labelEvery={block.labelEvery}
          value={shown}
          onChange={done ? undefined : (v) => { if (check.status === "wrong") check.retry(); setValue(v); }}
        />
        <p className="text-center font-bold text-lg mt-1">{shown === null ? "Pick a spot" : `Marker at ${fmt(shown)}`}</p>
      </div>
      <CheckFooter
        check={check}
        canSubmit={value !== null}
        onSubmit={submit}
        hint={block.hint}
        explanation={block.explanation}
        feedbackWrong={value !== null && !done ? (value < block.target ? "Too small. Slide it further right." : "Too big. Slide it back to the left.") : undefined}
        revealAnswer={<p>The marker now sits on <strong>{fmt(block.target)}</strong>.</p>}
      />
    </div>
  );
}
